/* eslint-disable */
import React, { useEffect, useState } from 'react';
import { withStyles } from '@material-ui/core';
import { useDrag } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import PropTypes from 'prop-types';
import cx from 'classnames';
import styles from './styles';
import {
  DispatchStatus, DispatchDragNDropTypes, DispatchOperationType, DispatchOperationTypeName, EquipIconMap,
} from '~/utils';
import PF2MDispatchIconMaker from '../PF2MDispatchIconMaker';
import PF2MTooltip from '../PF2MTooltip';
import { useTranslation } from 'react-i18next';
import {
  formatDateTime,
  formatDurationAsTime,
  calculateDurationBytimestamp,
  parseTimestamp,
} from '~/utils/moment';
import moduleIcons from '~/utils/modulesIcons';

const TRUCK = 2;

const statusClasses = {
  [DispatchStatus.OP]: 'greenLabel',
  [DispatchStatus.PO]: 'yellowLabel',
  [DispatchStatus.MA]: 'redLabel',
  [DispatchStatus.IN]: 'yellowLabel',
  [DispatchOperationType.LOAD_TRAFFIC]: 'grayLabel',
  [DispatchOperationType.LOAD_QUEUE]: 'brownLabel',
  [DispatchOperationType.LOAD]: 'limeLabel',
  [DispatchOperationType.UNLOAD_TRAFFIC]: 'blackLabel',
  [DispatchOperationType.UNLOAD_QUEUE]: 'brownLabel',
  [DispatchOperationType.UNLOAD]: 'greenLabel',
  [DispatchOperationType.LOAD_MANEUVER]: 'blueLabel',
  [DispatchOperationType.UNLOAD_MANEUVER]: 'blueLabel',
  [DispatchOperationType.EMPTY_STOP]: 'yellowLabel',
  [DispatchOperationType.RELEASED_EQUIPMENT]: 'yellowLabel',
};

const statusIcons = (status) => {
  const icons = {
    [DispatchStatus.OP]: { main: 'truckFull', warning: 'none' },
    [DispatchStatus.PO]: { main: 'truckEmpty', warning: 'pause' },
    [DispatchStatus.MA]: { main: 'truckEmpty', warning: 'maintenance' },
    [DispatchStatus.IN]: { main: 'truckEmpty', warning: 'pause' },
    [DispatchOperationType.LOAD_TRAFFIC]: { main: 'truckEmpty', warning: 'none' },
    [DispatchOperationType.LOAD_QUEUE]: { main: 'truckEmpty', warning: 'time' },
    [DispatchOperationType.LOAD]: { main: 'truckEmpty', warning: 'none' },
    [DispatchOperationType.UNLOAD_TRAFFIC]: { main: 'truckFull', warning: 'none' },
    [DispatchOperationType.UNLOAD_QUEUE]: { main: 'truckFull', warning: 'time' },
    [DispatchOperationType.UNLOAD]: { main: 'truckUnloading', warning: 'none', isBackground: true },
    [DispatchOperationType.LOAD_MANEUVER]: { main: 'truckEmpty', warning: 'steering' },
    [DispatchOperationType.UNLOAD_MANEUVER]: { main: 'truckFull', warning: 'steering' },
    [DispatchOperationType.EMPTY_STOP]: { main: 'truckEmpty', warning: 'pause' },
    [DispatchOperationType.RELEASED_EQUIPMENT]: { main: 'truckFull', warning: 'pause' },
  };
  if (icons[status] === undefined) {
    return { main: 'truckEmpty', warning: 'none' };
  }
  return icons[status];
};

const PF2MEquipIcon = ({
  item, itemType, customStyles, classes, reverse,
  useOperationType, extraInfo,
  onContextMenu,
  footer,
  header,
  height,
  width,
  tooltipData,
  dynamicIcon,
  v2,
  operationTypeEffective,
  ...rest
}) => {
  const iconTimestamp = (
    tooltipData?.start_timestamp ? tooltipData.start_timestamp : tooltipData.durations
  );

  const [calculatedDuration, setCalculatedDuration] = useState(
    tooltipData?.start_timestamp
      ? calculateDurationBytimestamp(iconTimestamp)
      : formatDurationAsTime(iconTimestamp)
  )

  const [, drag, preview] = useDrag({
    item: { type: itemType, obj: item },
    collect: monitor => ({
      isDragging: !!monitor.isDragging(),
    }),
  });
  const { t: translate } = useTranslation();

  const isV2 = v2;

  useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: true });
  }, [preview]);

  useEffect(() => {
    if (!isV2 || !tooltipData) return;

    const updateDuration = () => {
      if (tooltipData?.start_timestamp) {
        setCalculatedDuration(calculateDurationBytimestamp(iconTimestamp));
      } else {
        setCalculatedDuration(formatDurationAsTime(iconTimestamp));
      }
    }

    updateDuration()
    const intervalId = setInterval(updateDuration, 3000);

    return () => clearInterval(intervalId);
  }, [
    isV2,
    tooltipData?.start_timestamp,
    tooltipData?.durations,
    iconTimestamp,
    calculatedDuration,
    calculateDurationBytimestamp,
    formatDurationAsTime
  ]);

  const baseKey = useOperationType ? 'operation_type' : 'status';

  const activeKey = item[baseKey] === 'OP' || useOperationType
    ? 'operation_type'
    : 'status';

  const statusProp = operationTypeEffective ? baseKey : activeKey;

  const tooltipText = tooltipData ? (
    <div>
      <div>
        <strong>{`${translate('common:Equipment')}: `}</strong>
        <span>{tooltipData.equip_name}</span>
      </div>
      <div>
        <strong>{`${translate('common:LastServerConnection')}: `}</strong>
        <span>{formatDateTime(parseTimestamp(tooltipData.timestamp_sync_request / 1))}</span>
      </div>
      <div>
        <strong>
          {`${translate('common:OriginPoint')}: `}
        </strong>
        <span>{tooltipData.current_origin_point_name}</span>
      </div>
      <div >
        <strong>
          {`${translate('common:Operator')}: `}
        </strong>
        <span>
          {tooltipData.operators_name}
        </span>
      </div>
      <div>
        <strong>
          {`${translate('common:Code')}: `}
        </strong>
        <span>{tooltipData.codes_name}</span>
      </div>
      <div>
        <strong>
          {`${translate('common:Material')}: `}
        </strong>
        <span>{tooltipData.current_material_name}</span>
      </div>
      <div>
        <strong>
          {`${translate('common:Duration')}: `}
        </strong>
        <span>
          {isV2
            ? calculatedDuration
            : formatDurationAsTime(tooltipData?.durations)}
        </span>
      </div>
      {tooltipData.equip_type_id === 2 && (
        <div>
          <strong>
            {`${translate('common:CurrentCycleStep')}: `}
          </strong>
          <span>{DispatchOperationTypeName[tooltipData.operation_type]}</span>
        </div>
      )}
    </div>
  ) : <strong>{`${translate('common:DataUnavailable')}`}</strong>;

  const iconReverse = item.operation_type === 4 ? !reverse : reverse;

  const getDisplayValidationType = (equipItem) => {
    const validateOperationTypeLoading = equipItem.operation_type === 4
      && equipItem.current_element_point_id !== equipItem.current_origin_point_id;

    const validateOperationTypeDumping = equipItem.operation_type === 7
      && equipItem.current_element_point_id !== equipItem.current_destination_point_id;

    if (validateOperationTypeLoading) {
      return 'flex';
    } if (validateOperationTypeDumping) {
      return 'flex';
    }
    return 'none';
  };

  const styleTypeWarning = getDisplayValidationType(item);

  const renderHeader = text => (
    <div
      className={classes.labelTypeSelector}
      style={{
        display: 'flex',
        justifyContent: 'center',
        width: '100%',
        fontSize: 12,
        letterSpacing: 0.5,
        color: '#647886',
      }}
    >
      {text}
    </div>
  );

  const parsedExtraInfo = String(extraInfo);

  const dataIconInfo = {
    operationType: item.operation_type,
    operationTypeName: DispatchOperationTypeName[item.operation_type],
    equip_name: tooltipData?.equip_name || item?.equip_name,
    code_id: ((tooltipData?.codes_id?.[0] ?? tooltipData?.codes_id) || item?.code_id),
    equip_type_id: (item?.equip_type_id || tooltipData?.equip_type_id),
    status: tooltipData?.status || item?.status,
  };

  const getIconName = (item) => {
    if (item.equip_type_id === 2){
      return statusIcons(item[statusProp]).main
    }
    return EquipIconMap[item?.equip_type_id] || 'excavator'
  }

  return (
    <div
      onContextMenu={event => onContextMenu && onContextMenu(event, item)}
      style={{
        width: 70,
        height,
        display: 'flex',
        flexDirection: 'column',
        gap: 5,
        alignItems: 'center',
        cursor: 'pointer',
        ...customStyles,
      }}
      role="cell"
      ref={drag}
      {...rest}
    >
      {header !== undefined ? header : (
        item.equip_type_id === TRUCK && (
          parsedExtraInfo !== 'null'
            ? (
              <PF2MTooltip
                title={parsedExtraInfo}
                PopperProps={{ disablePortal: true }}
              >
                {renderHeader(parsedExtraInfo)}
              </PF2MTooltip>
            )
            : renderHeader('')
        )
      )}
      <PF2MTooltip
        title={tooltipData?.equip_name ? tooltipText : ''}
        PopperProps={{ disablePortal: true }}
        placement="top"
        classes={{
          tooltip: classes.tooltip
        }}
      >
        <div className={classes.iconContainer} style={{ gap: 5 }}>
          <div style={{
            position: 'relative',
            top: 0,
            left: 0,
          }}
          >
            <PF2MDispatchIconMaker
              iconName={getIconName(item)}
              settings={{
                iconSize: 50,
                isBackground: statusIcons(item[statusProp]).isBackground,
                reverse: iconReverse,
                alt: item.equip_name,
                data: dataIconInfo,
                warnings: (() => {
                  const firstWarning = statusIcons(item[statusProp]).warning !== styleTypeWarning
                    ? {
                      icon: statusIcons(item[statusProp]).warning,
                      position: iconReverse ? 'top-right' : 'top-left',
                      alt: 'Atenção',
                    }
                    : null;
                  const secondWarning = firstWarning?.icon !== 'none' && styleTypeWarning === 'flex'
                    ? {
                      icon: 'attentionAnomaly',
                      position: iconReverse ? 'top-left' : 'top-right',
                      alt: 'Atenção',
                    }
                    : null;
                  return [firstWarning, secondWarning].filter(Boolean);
                })(),
              }}
            />
          </div>
          {dynamicIcon
            && (
              <div className={classes.containerDynamicIcon} style={{ margin: 0 }}>
                <moduleIcons.DynamicCustom fontSize="small" />
              </div>
            )}
        </div>
      </PF2MTooltip>

      {footer !== undefined ? footer : (
        <PF2MTooltip
          title={item.equip_name}
          PopperProps={{ disablePortal: true }}
        >
          <div
            className={cx(classes.labelTypeSelector,
              classes[statusClasses[item[statusProp]]]
                ? classes[statusClasses[item[statusProp]]]
                : classes.greenLabel)}
            style={{
              maxWidth: 68,
              textAlign: 'center',
              fontSize: 12,
              letterSpacing: 0.8,
              padding: '0 5px',
              whiteSpace: 'nowrap',
            }}
          >
            {(item.equip_name || '').slice(0, 10)}
          </div>
        </PF2MTooltip>
      )}
    </div>
  );
};

PF2MEquipIcon.propTypes = {
  classes: PropTypes.object,
  customStyles: PropTypes.object,
  itemType: PropTypes.string,
  item: PropTypes.object,
  onClick: PropTypes.func,
  status: PropTypes.string,
  reverse: PropTypes.bool,
  useOperationType: PropTypes.bool,
  extraInfo: PropTypes.string,
  onContextMenu: PropTypes.func,
  header: PropTypes.node,
  footer: PropTypes.node,
  height: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
  ]),
  width: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
  ]),
  tooltipData: PropTypes.array,
  dynamicIcon: PropTypes.bool,
  v2: PropTypes.bool,
  operationTypeEffective: PropTypes.bool,
};

PF2MEquipIcon.defaultProps = {
  classes: {},
  customStyles: {},
  status: DispatchStatus.OP,
  itemType: DispatchDragNDropTypes.TRUCK,
  item: {},
  onClick: () => null,
  reverse: false,
  useOperationType: false,
  extraInfo: '',
  onContextMenu: null,
  header: undefined,
  footer: undefined,
  height: undefined,
  width: undefined,
  tooltipData: {},
  dynamicIcon: false,
  v2: false,
  operationTypeEffective: false,
};

export default withStyles(styles)(PF2MEquipIcon);
